// src/writer/components/BatchGenerationModal.tsx
import React, { useState, useEffect } from 'react';
import { ProjectState } from '../types/project';
import { BookStructureTextElementKey } from '../types';
import { 
    BatchGenerationConfig, 
    BatchGenerationProgress, 
    createBatchGenerationPlan, 
    getSuggestedBatchConfigs,
    GENERATION_DEPENDENCIES 
} from '../utils/batchGeneration';
import { primaryButtonClass, secondaryButtonClass, actionButtonClass } from '../constants';

interface BatchGenerationModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectState: ProjectState;
    onStartGeneration: (config: BatchGenerationConfig) => void;
    isGenerating: boolean;
    generationProgress?: BatchGenerationProgress;
}

const BatchGenerationModal: React.FC<BatchGenerationModalProps> = ({
    isOpen,
    onClose,
    projectState,
    onStartGeneration,
    isGenerating,
    generationProgress
}) => {
    const [selectedElements, setSelectedElements] = useState<BookStructureTextElementKey[]>([]);
    const [respectDependencies, setRespectDependencies] = useState(true);
    const [skipExisting, setSkipExisting] = useState(true);
    const [selectedPreset, setSelectedPreset] = useState<string>('essential');

    const suggestedConfigs = getSuggestedBatchConfigs(projectState);
    
    useEffect(() => {
        if (selectedPreset && suggestedConfigs[selectedPreset as keyof typeof suggestedConfigs]) {
            setSelectedElements(suggestedConfigs[selectedPreset as keyof typeof suggestedConfigs].elements);
        }
    }, [selectedPreset]);

    const plan = createBatchGenerationPlan(projectState, {
        elements: selectedElements,
        respectDependencies,
        skipExisting,
        maxConcurrent: 1
    });

    const handleElementToggle = (element: BookStructureTextElementKey) => {
        setSelectedElements(prev => 
            prev.includes(element) 
                ? prev.filter(e => e !== element)
                : [...prev, element]
        );
        setSelectedPreset(''); // Clear preset when manually selecting
    };

    const handleStartGeneration = () => {
        const config: BatchGenerationConfig = {
            elements: selectedElements,
            respectDependencies,
            skipExisting,
            maxConcurrent: 1
        };
        onStartGeneration(config);
    };

    const getElementStatus = (element: BookStructureTextElementKey) => {
        if (generationProgress?.completedElements && generationProgress.currentElement === element) {
            return 'generating';
        }
        if (generationProgress?.failedElements.includes(element)) {
            return 'failed';
        }
        if (generationProgress?.skippedElements.includes(element)) {
            return 'skipped';
        }
        if (plan.toSkip.includes(element)) {
            return 'exists';
        }
        return 'pending';
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'generating': return '⚡';
            case 'completed': return '✅';
            case 'failed': return '❌';
            case 'skipped': return '⏭️';
            case 'exists': return '📄';
            default: return '⏳';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'generating': return 'text-blue-600 bg-blue-50';
            case 'completed': return 'text-green-600 bg-green-50';
            case 'failed': return 'text-red-600 bg-red-50';
            case 'skipped': return 'text-gray-600 bg-gray-50';
            case 'exists': return 'text-yellow-600 bg-yellow-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
                <div className="p-6 border-b bg-gray-50">
                    <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-bold text-gray-800">Batch AI Generation</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            disabled={isGenerating}
                        >
                            ×
                        </button>
                    </div>
                    <p className="text-gray-600 mt-2">
                        Generate multiple book structure elements at once with AI assistance
                    </p>
                </div>

                <div className="p-6 max-h-[70vh] overflow-y-auto">
                    {!isGenerating ? (
                        <div className="space-y-6">
                            {/* Preset Selection */}
                            <div>
                                <h3 className="text-lg font-semibold mb-3">Quick Presets</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    {Object.entries(suggestedConfigs).map(([key, config]) => (
                                        <button
                                            key={key}
                                            onClick={() => setSelectedPreset(key)}
                                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                                                selectedPreset === key
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-200 hover:border-blue-300'
                                            }`}
                                        >
                                            <h4 className="font-medium text-gray-800">{config.name}</h4>
                                            <p className="text-sm text-gray-600 mt-1">{config.description}</p>
                                            <p className="text-xs text-gray-500 mt-2">
                                                {config.elements.length} elements • {config.estimatedTime}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Custom Selection */}
                            <div>
                                <h3 className="text-lg font-semibold mb-3">Custom Selection</h3>
                                <div className="grid md:grid-cols-2 gap-3">
                                    {GENERATION_DEPENDENCIES
                                        .filter(dep => dep.condition ? dep.condition(projectState) : true)
                                        .sort((a, b) => a.priority - b.priority)
                                        .map(dep => {
                                            const status = getElementStatus(dep.element);
                                            const isSelected = selectedElements.includes(dep.element);
                                            
                                            return (
                                                <label
                                                    key={dep.element}
                                                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                                                        isSelected 
                                                            ? 'border-blue-500 bg-blue-50' 
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleElementToggle(dep.element)}
                                                        className="mr-3"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between">
                                                            <span className="font-medium text-gray-800">
                                                                {dep.element.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                            </span>
                                                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(status)}`}>
                                                                {getStatusIcon(status)} {status}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-gray-600 mt-1">
                                                            Priority: {dep.priority} • {dep.isOptional ? 'Optional' : 'Required'}
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                </div>
                            </div>

                            {/* Options */}
                            <div>
                                <h3 className="text-lg font-semibold mb-3">Generation Options</h3>
                                <div className="space-y-3">
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={respectDependencies}
                                            onChange={(e) => setRespectDependencies(e.target.checked)}
                                        />
                                        <span className="text-gray-700">Respect element dependencies</span>
                                    </label>
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={skipExisting}
                                            onChange={(e) => setSkipExisting(e.target.checked)}
                                        />
                                        <span className="text-gray-700">Skip elements with existing content</span>
                                    </label>
                                </div>
                            </div>

                            {/* Generation Plan Summary */}
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                <h4 className="font-medium text-blue-800 mb-2">Generation Plan</h4>
                                <div className="grid md:grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <span className="text-blue-700">Total Selected:</span>
                                        <span className="font-medium ml-2">{selectedElements.length}</span>
                                    </div>
                                    <div>
                                        <span className="text-blue-700">To Generate:</span>
                                        <span className="font-medium ml-2">{plan.toGenerate.length}</span>
                                    </div>
                                    <div>
                                        <span className="text-blue-700">Estimated Time:</span>
                                        <span className="font-medium ml-2">{Math.ceil(plan.estimatedTime / 60)} min</span>
                                    </div>
                                </div>
                                {plan.toSkip.length > 0 && (
                                    <p className="text-xs text-blue-600 mt-2">
                                        {plan.toSkip.length} elements will be skipped (already exist)
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Generation Progress */
                        <div className="space-y-6">
                            <div className="text-center">
                                <h3 className="text-xl font-semibold text-gray-800 mb-2">Generating Content...</h3>
                                <p className="text-gray-600">
                                    {generationProgress?.currentElement ? 
                                        `Currently generating: ${generationProgress.currentElement}` : 
                                        'Preparing generation...'
                                    }
                                </p>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-gray-200 rounded-full h-3">
                                <div 
                                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                                    style={{ 
                                        width: `${generationProgress ? 
                                            (generationProgress.completedElements / generationProgress.totalElements) * 100 : 
                                            0}%` 
                                    }}
                                ></div>
                            </div>

                            <div className="text-center text-sm text-gray-600">
                                {generationProgress?.completedElements || 0} of {generationProgress?.totalElements || 0} completed
                            </div>

                            {/* Element Status List */}
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {selectedElements.map(element => {
                                    const status = getElementStatus(element);
                                    return (
                                        <div 
                                            key={element}
                                            className={`flex items-center justify-between p-3 rounded-lg ${getStatusColor(status)}`}
                                        >
                                            <span className="font-medium">
                                                {element.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </span>
                                            <span className="text-sm">
                                                {getStatusIcon(status)} {status}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t bg-gray-50 flex justify-between">
                    <button
                        onClick={onClose}
                        className={secondaryButtonClass}
                        disabled={isGenerating}
                    >
                        {isGenerating ? 'Cancel' : 'Close'}
                    </button>
                    
                    {!isGenerating && (
                        <button
                            onClick={handleStartGeneration}
                            disabled={selectedElements.length === 0 || plan.toGenerate.length === 0}
                            className={`${actionButtonClass} ${
                                selectedElements.length === 0 || plan.toGenerate.length === 0 
                                    ? 'opacity-50 cursor-not-allowed' 
                                    : ''
                            }`}
                        >
                            Generate {plan.toGenerate.length} Elements
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BatchGenerationModal;